import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { CategorySubcategorySelect } from './CategorySubcategorySelect';
import { RulePreviewModal } from './RulePreviewModal';

interface RuleCreatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRuleCreated: () => void;
  /** Success message on parent after modal closes (e.g. Transactions inline banner) */
  onActionMessage?: (message: string) => void;
  transaction?: {
    id: number;
    description: string;
    merchant_name: string | null;
    amount: number;
    linked_account_id: number;
    category_id: number | null;
    subcategory_id: number | null;
  } | null;
  /** Pre-select a category (used when opening from a rule suggestion) */
  prefillCategoryId?: number | null;
  /** Pre-select a subcategory (used when opening from a rule suggestion) */
  prefillSubcategoryId?: number | null;
  /** Seed an initial condition overriding the transaction-derived defaults (used when opening from a rule suggestion) */
  prefillCondition?: { field_type: 'merchant' | 'description'; operator: 'equals' | 'starts_with'; value: string } | null;
  categories: Array<{ id: number; name: string; subcategories: Array<{ id: number; name: string; }>; }>;
}

interface RuleCondition {
  field_type: 'merchant' | 'description' | 'amount' | 'account';
  operator: 'contains' | 'equals' | 'starts_with' | 'ends_with' | 'regex' | 'greater_than' | 'less_than';
  value: string;
  condition_order: number;
  logic_operator: 'AND' | 'OR';
}

interface ExistingRule {
  id: number;
  rule_name: string;
  rule_type: string;
  category_id: number;
  subcategory_id: number | null;
  confidence: number;
  conditions: RuleCondition[];
  created_at: string;
  category_name?: string;
  subcategory_name?: string;
}

export const RuleCreatorModal: React.FC<RuleCreatorModalProps> = ({
  isOpen,
  onClose,
  onRuleCreated,
  onActionMessage,
  transaction,
  prefillCategoryId,
  prefillSubcategoryId,
  prefillCondition,
  categories
}) => {
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | null>(null);
  const [conditions, setConditions] = useState<RuleCondition[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<{
    matching_transactions: any[];
    total_matches: number;
    total_uncategorized: number;
    already_categorized_matches: number;
    sign_flip_matches: number;
    sign_flip_conditions: RuleCondition[] | null;
  } | null>(null);
  
  // Tab and existing rules state
  const [activeTab, setActiveTab] = useState<'create' | 'existing'>('create');
  const [existingRules, setExistingRules] = useState<ExistingRule[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [matchingRuleIds, setMatchingRuleIds] = useState<number[]>([]);
  const [modalError, setModalError] = useState<string | null>(null);

  // Fetch existing rules when modal opens
  const fetchExistingRules = async () => {
    setIsLoadingRules(true);
    try {
      const rules = await api.transactions.getRules();
      setExistingRules(rules);
      
      // Calculate matching rules if transaction is provided
      if (transaction) {
        const matchingIds = rules.filter((rule: ExistingRule) => {
          return rule.conditions.some((condition: RuleCondition) => {
            if (condition.field_type === 'description' && condition.operator === 'equals') {
              return condition.value === transaction.description;
            }
            if (condition.field_type === 'merchant' && condition.operator === 'equals') {
              return condition.value === transaction.merchant_name;
            }
            if (condition.field_type === 'amount' && condition.operator === 'equals') {
              return parseFloat(condition.value) === transaction.amount;
            }
            return false;
          });
        }).map((rule: ExistingRule) => rule.id);
        setMatchingRuleIds(matchingIds);
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setIsLoadingRules(false);
    }
  };

  // Initialize form based on transaction and fetch rules
  useEffect(() => {
    if (isOpen) {
      setModalError(null);
      fetchExistingRules();
      // Prevent body scroll when modal is open
      document.body.classList.add('modal-open');
    } else {
      // Re-enable body scroll when modal is closed
      document.body.classList.remove('modal-open');
    }
    
    // Cleanup function to remove class when component unmounts
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);
    
  useEffect(() => {
    if (transaction && isOpen) {
      // Pre-populate category: prefer explicit prefill (suggestion flow), then transaction's own category
      if (prefillCategoryId) {
        setSelectedCategory(prefillCategoryId);
        if (prefillSubcategoryId) {
          setSelectedSubcategory(prefillSubcategoryId);
        }
      } else if (transaction.category_id) {
        setSelectedCategory(transaction.category_id);
        if (transaction.subcategory_id) {
          setSelectedSubcategory(transaction.subcategory_id);
        }
      }

      // When a prefillCondition is provided (from post-categorize rule suggestion), use it as
      // the sole seed condition so the modal opens pre-aimed at the cluster rather than the
      // specific transaction's description/amount.
      if (prefillCondition) {
        setConditions([{
          field_type: prefillCondition.field_type,
          operator: prefillCondition.operator,
          value: prefillCondition.value,
          condition_order: 1,
          logic_operator: 'AND',
        }]);
        return;
      }

      // Create conditions based on transaction data
      const newConditions: RuleCondition[] = [];

      // Always add description condition
      if (transaction.description) {
        newConditions.push({
          field_type: 'description',
          operator: 'equals',
          value: transaction.description,
          condition_order: newConditions.length + 1,
          logic_operator: 'AND'
        });
      }

      // Add amount condition if transaction is categorized (more specific)
      if (transaction.category_id && transaction.amount) {
        newConditions.push({
          field_type: 'amount',
          operator: 'equals',
          value: transaction.amount.toString(),
          condition_order: newConditions.length + 1,
          logic_operator: 'AND'
        });
      }

      // Add merchant condition if available
      if (transaction.merchant_name) {
        newConditions.push({
          field_type: 'merchant',
          operator: 'equals',
          value: transaction.merchant_name,
          condition_order: newConditions.length + 1,
          logic_operator: 'AND'
        });
      }

      setConditions(newConditions);
    }
  }, [transaction, isOpen, prefillCategoryId, prefillSubcategoryId, prefillCondition]);

  const handlePreviewRule = async () => {
    if (!selectedCategory || conditions.length === 0) return;

    setIsCreating(true);
    try {
      const previewRequest = {
        rule_type: 'complex' as const,
        conditions
      };

      const preview = await api.transactions.previewRuleMatches(previewRequest);
      setPreviewData(preview);
      setShowPreview(true);
    } catch (error) {
      console.error('Error previewing rule:', error);
      setModalError('Failed to preview rule. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateRule = async () => {
    if (!selectedCategory || conditions.length === 0) return;

    setIsCreating(true);
    try {
      // Generate rule name from conditions
      const ruleName = conditions.map((condition, index) => {
        const prefix = index > 0 ? ` ${condition.logic_operator} ` : '';
        return `${prefix}${condition.field_type} ${condition.operator} "${condition.value}"`;
      }).join('');

      const ruleData = {
        rule_name: ruleName,
        rule_type: 'complex' as const,
        category_id: selectedCategory,
        subcategory_id: selectedSubcategory,
        confidence: 0.9, // Default confidence
        conditions
      };

      await api.transactions.createRule(ruleData);

      onRuleCreated();
      onClose();
      setShowPreview(false);
      onActionMessage?.('Rule created successfully.');
    } catch (error) {
      console.error('Error creating rule:', error);
      setModalError('Failed to create rule. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateFlippedRule = async () => {
    const flippedConditions = previewData?.sign_flip_conditions;
    if (!selectedCategory || !flippedConditions) return;

    setIsCreating(true);
    try {
      const ruleName = flippedConditions.map((condition: RuleCondition, index: number) => {
        const prefix = index > 0 ? ` ${condition.logic_operator} ` : '';
        return `${prefix}${condition.field_type} ${condition.operator} "${condition.value}"`;
      }).join('');

      await api.transactions.createRule({
        rule_name: ruleName,
        rule_type: 'complex' as const,
        category_id: selectedCategory,
        subcategory_id: selectedSubcategory,
        confidence: 0.9,
        conditions: flippedConditions,
      });

      onRuleCreated();
      onClose();
      setShowPreview(false);
      onActionMessage?.('Rule created successfully.');
    } catch (error) {
      console.error('Error creating rule:', error);
      setModalError('Failed to create rule. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const addCondition = () => {
    const newCondition: RuleCondition = {
      field_type: 'description',
      operator: 'contains',
      value: '',
      condition_order: conditions.length + 1,
      logic_operator: 'AND'
    };
    setConditions([...conditions, newCondition]);
  };

  const updateCondition = (index: number, field: keyof RuleCondition, value: any) => {
    const updated = [...conditions];
    updated[index] = { ...updated[index], [field]: value };
    setConditions(updated);
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const handleDeleteRule = async (ruleId: number) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      await api.transactions.deleteRule(ruleId);
      await fetchExistingRules(); // Refresh the list
      onRuleCreated(); // Notify parent component (no categorization data for deletion)
    } catch (error) {
      console.error('Error deleting rule:', error);
      setModalError('Failed to delete rule. Please try again.');
    }
  };


  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="rule-creator-modal">
        <div className="modal-header">
          <h2>Categorization Rules</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        {modalError && (
          <div
            role="alert"
            style={{
              margin: '0 1rem',
              padding: '10px 12px',
              borderRadius: '8px',
              background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.4)',
              color: 'var(--danger-color)',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <span>{modalError}</span>
            <button
              type="button"
              onClick={() => setModalError(null)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'inherit',
                fontSize: '16px',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Transaction Preview - Compact single row */}
        {transaction && (
          <div className="transaction-preview-compact">
            <span className="transaction-label">Based on:</span>
            <span className="transaction-desc">{transaction.description}</span>
            <span className="transaction-amount">${transaction.amount}</span>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            Create New Rule
          </button>
          <button 
            className={`tab-btn ${activeTab === 'existing' ? 'active' : ''}`}
            onClick={() => setActiveTab('existing')}
          >
            Existing Rules ({existingRules.length})
          </button>
        </div>

        <div className="modal-body">
          {activeTab === 'create' && (
            <div className="rule-builder">
              <div className="conditions-section">
                <h3>Rule Conditions</h3>
                {conditions.map((condition, index) => (
                  <div key={index} className="condition-row">
                    {index > 0 && (
                      <select
                        value={condition.logic_operator}
                        onChange={(e) => updateCondition(index, 'logic_operator', e.target.value)}
                        className="form-select logic-operator"
                      >
                        <option value="AND">AND</option>
                        <option value="OR">OR</option>
                      </select>
                    )}
                    
                    <select
                      value={condition.field_type}
                      onChange={(e) => updateCondition(index, 'field_type', e.target.value)}
                      className="form-select field-select"
                    >
                      <option value="merchant">Merchant</option>
                      <option value="description">Description</option>
                      <option value="amount">Amount</option>
                      <option value="account">Account</option>
                    </select>

                    <select
                      value={condition.operator}
                      onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                      className="form-select operator-select"
                    >
                      <option value="contains">contains</option>
                      <option value="equals">equals</option>
                      <option value="starts_with">starts with</option>
                      <option value="ends_with">ends with</option>
                      <option value="regex">matches regex</option>
                      <option value="greater_than">greater than</option>
                      <option value="less_than">less than</option>
                    </select>

                    <input
                      type="text"
                      value={condition.value}
                      onChange={(e) => updateCondition(index, 'value', e.target.value)}
                      placeholder="Value"
                      className="form-input value-input"
                    />

                    <button
                      onClick={() => removeCondition(index)}
                      className="btn btn-danger btn-sm remove-btn"
                      title="Remove condition"
                    >
                      ×
                    </button>
                  </div>
                ))}
                
                <button onClick={addCondition} className="btn btn-secondary add-condition-btn">
                  + Add Condition
                </button>
              </div>

              <div className="category-section">
                <h3>Category</h3>
                <div className="form-group">
                  <label>Category & Subcategory</label>
                  <CategorySubcategorySelect
                    categories={categories}
                    currentCategoryId={selectedCategory}
                    currentSubcategoryId={selectedSubcategory}
                    isIncome={false} // We'll handle both income and expense categories
                    placeholder="Select category..."
                    onChange={(categoryId, subcategoryId) => {
                      setSelectedCategory(categoryId);
                      setSelectedSubcategory(subcategoryId);
                    }}
                  />
                </div>
              </div>

            </div>
          )}

          {activeTab === 'existing' && (
            <div className="existing-rules">
              <h3>Existing Rules</h3>
              {isLoadingRules ? (
                <div className="loading">Loading rules...</div>
              ) : existingRules.length === 0 ? (
                <div className="no-rules">No rules found. Create your first rule!</div>
              ) : (
                <div className="rules-list">
                  {existingRules.map((rule) => (
                    <div key={rule.id} className={`rule-item ${matchingRuleIds.includes(rule.id) ? 'matching-rule' : ''}`}>
                      <div className="rule-content">
                        <div className="rule-conditions">
                          {rule.conditions.map((condition, index) => (
                            <div key={index} className="condition-line">
                              {index > 0 && (
                                <span className="logic-operator">{condition.logic_operator}</span>
                              )}
                              <span className="condition-text">
                                {condition.field_type} {condition.operator} "{condition.value}"
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="rule-category">
                          {rule.category_name || 'Unknown'}
                          {rule.subcategory_name && ` → ${rule.subcategory_name}`}
                        </div>
                      </div>
                      <div className="rule-actions">
                        {matchingRuleIds.includes(rule.id) && (
                          <span className="match-indicator">MATCHES</span>
                        )}
                        <button
                          onClick={() => handleDeleteRule(rule.id)}
                          className="btn btn-danger btn-sm"
                          title="Delete rule"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            {activeTab === 'create' ? 'Cancel' : 'Close'}
          </button>
          {activeTab === 'create' && (
            <button
              onClick={handlePreviewRule}
              disabled={!selectedCategory || conditions.length === 0 || isCreating}
              className="btn btn-primary"
            >
              {isCreating ? 'Loading Preview...' : 'Preview & Create Rule'}
            </button>
          )}
        </div>
      </div>

      {/* Rule Preview Modal */}
      {showPreview && previewData && (
        <RulePreviewModal
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          onConfirm={handleCreateRule}
          ruleName={conditions.map((condition, index) => {
            const prefix = index > 0 ? ` ${condition.logic_operator} ` : '';
            return `${prefix}${condition.field_type} ${condition.operator} "${condition.value}"`;
          }).join('')}
          categoryName={categories.find(cat => cat.id === selectedCategory)?.name || ''}
          subcategoryName={selectedSubcategory ? 
            categories.find(cat => cat.id === selectedCategory)?.subcategories?.find(sub => sub.id === selectedSubcategory)?.name 
            : undefined}
          matchingTransactions={previewData.matching_transactions}
          totalMatches={previewData.total_matches}
          totalUncategorized={previewData.total_uncategorized}
          alreadyCategorizedMatches={previewData.already_categorized_matches ?? 0}
          signFlipMatches={previewData.sign_flip_matches ?? 0}
          signFlipConditions={previewData.sign_flip_conditions ?? undefined}
          onConfirmFlipped={handleCreateFlippedRule}
        />
      )}
    </div>
  );
};
